// Shared GeoJSON helpers for Mapbox-based pricing editors.
// Pure functions — no React, no DOM, no Mapbox runtime dependency.

const BUDAPEST = { latitude: 47.4979, longitude: 19.0402 };

export const DEFAULT_CENTER = BUDAPEST;

/**
 * Build a GeoJSON Polygon Feature that approximates a circle.
 *
 * @param {[number, number]} center [longitude, latitude]
 * @param {number} radiusKm
 * @param {number} [points=64] number of vertices used to approximate the circle
 * @returns {object} GeoJSON Feature
 */
export const createGeoJSONCircle = (center, radiusKm, points = 64) => {
  const coords = { latitude: center[1], longitude: center[0] };
  const km = radiusKm;
  const ret = [];
  const distanceX = km / (111.32 * Math.cos((coords.latitude * Math.PI) / 180));
  const distanceY = km / 110.574;

  for (let i = 0; i < points; i += 1) {
    const theta = (i / points) * (2 * Math.PI);
    const x = distanceX * Math.cos(theta);
    const y = distanceY * Math.sin(theta);
    ret.push([coords.longitude + x, coords.latitude + y]);
  }
  ret.push(ret[0]);

  return {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [ret] },
  };
};

/**
 * Build a GeoJSON Polygon Feature from an array of `{ latitude, longitude }`.
 * Returns null when fewer than 3 vertices are provided.
 */
export const createGeoJSONPolygon = (coordinates) => {
  if (!coordinates || coordinates.length < 3) return null;

  const coords = [...coordinates];
  const first = coords[0];
  const last = coords[coords.length - 1];
  if (first.longitude !== last.longitude || first.latitude !== last.latitude) {
    coords.push({ ...first });
  }

  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [coords.map((c) => [c.longitude, c.latitude])],
    },
  };
};

/**
 * Average of the input vertices. Falls back to Budapest when empty.
 */
export const calculateCentroid = (coordinates) => {
  if (!coordinates || coordinates.length === 0) return { ...BUDAPEST };

  const sum = coordinates.reduce(
    (acc, c) => ({
      latitude: acc.latitude + c.latitude,
      longitude: acc.longitude + c.longitude,
    }),
    { latitude: 0, longitude: 0 },
  );

  return {
    latitude: sum.latitude / coordinates.length,
    longitude: sum.longitude / coordinates.length,
  };
};

/**
 * Convert a GeoJSON Polygon/MultiPolygon (as returned by Nominatim's region
 * search) into our `{ latitude, longitude }[]` shape. Down-samples to ~100
 * points to keep the map responsive.
 */
export const extractPolygonCoordinates = (geojson) => {
  if (!geojson) return [];

  let coords = [];
  if (geojson.type === 'Polygon') {
    [coords] = geojson.coordinates;
  } else if (geojson.type === 'MultiPolygon') {
    let largestRing = [];
    geojson.coordinates.forEach((polygon) => {
      const [outerRing] = polygon;
      if (outerRing.length > largestRing.length) {
        largestRing = outerRing;
      }
    });
    coords = largestRing;
  }

  if (coords.length > 100) {
    const step = Math.ceil(coords.length / 100);
    coords = coords.filter((_, i) => i % step === 0);
  }

  return coords.map((coord) => ({ latitude: coord[1], longitude: coord[0] }));
};
